import React, { useEffect, useState } from 'react';
import { Grid, Box } from '@mui/material';
import dayjs from 'dayjs';

import PageHeader from '../../../components/PageHeader/PageHeader';
import MonthYearSelector from '../../../components/Reports/Sales/MonthYearSelector';
import { useTranslation } from 'react-i18next';
import { getBranchDashboardData } from '../../../services/ReportsBranchService';
import {
  MonetizationOn, Receipt, BarChart, TrendingUp,
} from '@mui/icons-material';
import BranchParticipationPieChart from '../../../components/Reports/Branch/BranchParticipationPieChart';
import DefinedVsUndefinedChart from '../../../components/Reports/Branch/DefinedVsUndefinedChart';
import AverageTicketByBranchChart from '../../../components/Reports/Branch/AverageTicketByBranchChart';
import PaymentMethodsByBranchChart from '../../../components/Reports/Branch/PaymentMethodsByBranchChart';
import BranchMonthlyGrowthChart from '../../../components/Reports/Branch/BranchMonthlyGrowthChart';
import SummaryCards from '../../../components/Reports/SummaryCards';
import { formatMoney } from '../../../utils/currency';

import useReportCurrency from '../../../hooks/useReportCurrency';
import ReportCurrencyPicker from '../../../components/Currency/ReportCurrencyPicker';
const BranchReports = () => {
  const [reportCurrency, setReportCurrency] = useReportCurrency();
  const { t } = useTranslation();
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const result = await getBranchDashboardData({ month, year });
      setData(result);
      setLoading(false);
    };

    fetch();
  }, [month, year, reportCurrency]);

  const summaryItems = data?.summary
  ? [
      {
        title: 'Más ventas',
        icon: <MonetizationOn color="primary" fontSize="large" />,
        value: `${getBranchName(data.summary.topSeller.branchId)} – ${formatMoney(data.summary.topSeller.total)}`,
      },
      {
        title: 'Más tickets',
        icon: <Receipt color="success" fontSize="large" />,
        value: `${getBranchName(data.summary.topTickets.branchId)} – ${data.summary.topTickets.tickets} tickets`,
      },
      {
        title: 'Mejor ticket promedio',
        icon: <BarChart color="action" fontSize="large" />,
        value: `${getBranchName(data.summary.topAvgTicket.branchId)} – ${formatMoney(data.summary.topAvgTicket.average)}`,
      },
      {
        title: 'Mayor crecimiento',
        icon: <TrendingUp color="secondary" fontSize="large" />,
        value: `${getBranchName(data.summary.topGrowth.branchId)} – ↑ ${data.summary.topGrowth.growthPercent.toFixed(1)}%`,
      },
    ]
  : [];

  function getBranchName(id) {
    return data.branches.find(b => b.id === id)?.name || `Sucursal ${id}`;
  }

  const transformMonthlyGrowthData = (monthlyGrowth) =>
  monthlyGrowth.map(item => ({
    month: item.month,
    ...item.values
  }));

  return (
    <Box p={3}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item>
          <PageHeader title={t('branches')} subtitle={t('Comparación entre sucursales')} />
      <ReportCurrencyPicker currency={reportCurrency} onChange={setReportCurrency} />
        </Grid>
        <Grid item>
          <MonthYearSelector month={month} setMonth={setMonth} year={year} setYear={setYear} />
        </Grid>
      </Grid>

      <Box mt={2}>
        <SummaryCards items={summaryItems} loading={loading} />
      </Box>

      <Grid container spacing={2} mt={0}>
        <Grid item xs={12} md={4}>
          <BranchParticipationPieChart
            data={data?.branchSalesDistribution || []}
            branches={data?.branches || []}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DefinedVsUndefinedChart
            data={data?.definedVsUndefined || []}
            branches={data?.branches || []}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <AverageTicketByBranchChart
            data={data?.averageSalesByBranch || []}
            branches={data?.branches || []}
            loading={loading}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={0} mb={6}>
        <Grid item xs={12} md={4}>
          <PaymentMethodsByBranchChart
            data={data?.paymentMethodsByBranch || []}
            branches={data?.branches || []}
            loading={loading}
          />
        </Grid>
        <Grid item xs={8}>
          <BranchMonthlyGrowthChart
            data={transformMonthlyGrowthData(data?.monthlyGrowth || [])}
            branches={data?.branches || []}
            loading={loading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BranchReports;
